/**
 * GTMEditor - Configuration editor for Google Tag Manager
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Button, Link, Alert } from '@mui/material';
import type { GTMConfig, PlatformConfig } from '../../types/analytics.types';

interface GTMEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const GTMEditor: React.FC<GTMEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const gtmConfig = config as GTMConfig;
  const [localConfig, setLocalConfig] = useState<GTMConfig>(gtmConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(gtmConfig);
    setLocalName(name || '');
  }, [gtmConfig, name]);

  const handleChange = useCallback(
    (field: keyof GTMConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <TextField
        label="Container ID"
        value={localConfig.containerId || ''}
        onChange={(e) => handleChange('containerId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="GTM-XXXXXXX"
        helperText={
          <>
            Find your Container ID in{' '}
            <Link href="https://tagmanager.google.com" target="_blank" rel="noopener">
              Google Tag Manager
            </Link>
          </>
        }
      />

      <TextField
        label="Data Layer Name"
        value={localConfig.dataLayerName || 'dataLayer'}
        onChange={(e) => handleChange('dataLayerName', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        sx={{ mb: 2 }}
        helperText="Default: dataLayer. Change only if you use a custom name."
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        All analytics events will be pushed to the dataLayer. Configure triggers in GTM to send
        them to your destinations.
      </Alert>

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.containerId}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
